<route>
{
  "meta": {
    "permission": [
      "invoices.view_invoice"
    ]
  }
}
</route>

<template>
  <v-container fluid>
    <v-card>
      <i-toolbar dontSave :title="$t('receivable')"></i-toolbar>
      <v-card-text>
        <!-- Cuenta por cobrar -->
        <div class="d-flex justify-content-between">
          <h3 class="title-responsive">
            <v-chip class="mb-1 text-light" :color="statusColor" x-small>
              {{ $tc(receivable.state, 1) }}
            </v-chip>
            <span class="primary--text">
              {{ receivable.code }}
            </span>
          </h3>
          <div class="d-flex g-2 align-items-center">
            {{ receivable.order_name }}
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <i-btn
                  color="secondary"
                  icon="fa-file-invoice"
                  outlined
                  :title="$tc('order', 1)"
                  @click="goToOrder(receivable.order)"
                  v-on="on"
                />
              </template>
            </v-tooltip>
          </div>
        </div>
        <p>{{ $t('creation_date') }}: {{ dateFormat(receivable.date_init) }}</p>
        <p v-if="receivable.date_end">
          {{ $t('end_date') }}: {{ dateFormat(receivable.date_end) }}
        </p>

        <v-row class="my-0">
          <!-- Cliente -->
          <v-col cols="12" md="6" lg="5">
            <div class="receivable-header-info">
              <h2 class="title-responsive primary--text">
                {{ $tc('customer', 1) }}
              </h2>
              <p v-if="receivable.customer.company_name">
                {{ receivable.customer.company_name }}
              </p>
              <p>
                {{ receivable.customer.first_name }}
                {{ receivable.customer.last_name }}
              </p>
              <p>{{ receivable.customer.phone }}</p>
              <p>{{ receivable.customer.email }}</p>
            </div>
          </v-col>

          <!-- Dirección de Cobro -->
          <v-col cols="12" md="6" lg="5">
            <div class="receivable-header-info h-100">
              <h2 class="title-responsive primary--text">
                {{ $t('billing_address') }}
              </h2>
              <p v-if="receivable.customer.address_set">
                {{ formatAddress(receivable.customer.address_set[0]) }}
              </p>
              <p v-if="receivable.customer.address_set">
                {{ receivable.customer.address_set[0].zip_code }}
              </p>
            </div>
          </v-col>
          <v-col cols="12" lg="2">
            <h3 class="title-responsive font-weight-normal text-right">
              {{ $t('balance') }}
            </h3>
            <p class="text-right">
              <i-money
                class="title-responsive primary--text"
                mode="text"
                :value="receivable.balance"
              />
            </p>
          </v-col>
        </v-row>

        <!-- Subórdenes de venta -->
        <v-row class="my-0">
          <v-col class="pb-5" cols="12">
            <div class="table-responsive">
              <v-data-table
                :headers="headers"
                :items="suborders"
                :loading="loadingSuborders"
                :show-select="suborders.length > 0"
                checkbox-color="secondary"
                hide-default-footer
                item-key="pk"
                :no-data-text="$t('no_suborders_outstanding')"
                sort-by="code"
                v-model="suborderSelected"
              >
                <template v-slot:item.total="{ value }">
                  <i-money :value="value" mode="text" />
                </template>
                <template v-slot:item.allocations="{ value }">
                  <i-money :value="sumPayments(value)" mode="text" />
                </template>
                <template v-if="suborders.length > 0" v-slot:header.pk>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <i-btn
                        color="primary"
                        icon="fa-money-bills"
                        outlined
                        :title="`${$tc('pay', 2)} ${$t('all')}`"
                        @click="selectAllSuborders(true)"
                        v-on="on"
                      />
                    </template>
                  </v-tooltip>
                </template>
                <template v-slot:item.pk="{ value }">
                  <div class="d-flex g-1 justify-content-center">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <i-btn
                          color="primary"
                          icon="fa-money-bill"
                          outlined
                          small
                          :title="`${$tc('pay', 2)} ${$tc('suborder', 1)}`"
                          @click="selectSuborder(value)"
                          v-on="on"
                        />
                      </template>
                    </v-tooltip>
                    <v-tooltip v-if="checkPermissions.isElevatedRole" bottom>
                      <template v-slot:activator="{ on }">
                        <i-btn
                          color="secondary"
                          icon="fa-pen-to-square"
                          outlined
                          small
                          :title="`${$t('open')} ${$tc('suborder', 1)}`"
                          @click="openSuborder(value)"
                          v-on="on"
                        />
                      </template>
                    </v-tooltip>
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-col>
        </v-row>

        <!-- Sección de pagos -->
        <payments
          v-if="receivable.pk"
          :balance="receivable.balance"
          :amount="sumSuborders(suborderSelected)"
          :receivable="receivable.pk"
          :allSuborders="allSuborders"
          :subordersSelected="suborderSelected"
          @paymentCreated="updateValues()"
        />
      </v-card-text>
    </v-card>

    <!-- Modal de subórdenes de venta -->
    <dial-prod v-if="checkPermissions.isElevatedRole" ref="dialprod" />
  </v-container>
</template>
<script>
import { mapGetters } from 'vuex'
import Payments from '@/components/receivable/ReceivablePayments.vue'
import DialProd from '@/components/sale/DialogProduct.vue'
export default {
  components: {
    DialProd,
    Payments
  },
  data: () => ({
    receivable: {
      order_name: '',
      balance: 0.0,
      customer: {},
      code: '',
      date_end: null,
      date_init: null,
      deadline: null,
      pk: null,
      state: 'outstanding'
    },
    loading: false,
    loadingSuborders: false,
    invoices: [],
    reload: false,
    dialog: false,
    toEdit: null,
    allSuborders: [],
    suborders: [],
    suborderSelected: []
  }),
  computed: {
    ...mapGetters({
      getPermissions: 'session/getPermissions',
      isAdmin: 'session/isAdmin'
    }),
    headers() {
      return [
        {
          text: this.$t('code'),
          value: 'code'
        },
        {
          text: this.$tc('suborder', 1),
          value: 'name'
        },
        {
          text: this.$tc('product', 1),
          value: 'own_prod'
        },
        {
          text: this.$tc('amount', 2),
          value: 'quantity'
        },
        {
          text: this.$tc('total', 1),
          value: 'total'
        },
        {
          text: this.$tc('paid', 1),
          value: 'allocations'
        },
        {
          align: 'center',
          sortable: false,
          text: this.$tc('action', 2),
          value: 'pk'
        }
      ]
    },
    /**
     * Determina si el usuario tiene los permisos necesarios según la indicación
     *
     * @returns {Object} Objeto con los permisos del usuario.
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    checkPermissions() {
      return {
        payment: this.getPermissions([`invoices.view_payments`]),
        isElevatedRole:
          this.getPermissions([`users.change_profile`]) &&
          this.getPermissions([`auth.change_user`])
      }
    },
    /**
     * Computado que determina el color con el que se mostrará el badge donde se
     * muestra el estado de la cuenta por cobrar.
     * @returns {String}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    statusColor() {
      const state = this.receivable.state
      if (state === 'paid') {
        return 'success'
      }
      if (state === 'outstanding') {
        return 'primary'
      }
      return 'error'
    }
  },
  methods: {
    open(item) {
      this.toEdit = item === undefined ? null : item
      this.dialog = true
    },
    updateValues() {
      this.getReceivable()
      this.suborderSelected = []
    },
    /**
     * Método para abrir la modal de subórdenes de venta
     * @param {Number} suborderPK   ID de la suborden de venta que se quiere
     *                              consultar.
     * @returns {Void}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    openSuborder(suborderPK) {
      this.$refs.dialprod.open(suborderPK)
    },
    /**
     * Método que consulta la cuenta por cobrar solicitada.
     *
     * @param {Object} pk ID de la orden de venta
     *
     * @returns {Array} Subórdenes de venta.
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    async getReceivable() {
      const validation = this.codification({
        mode: 'decode',
        code: this.$route.params.pk
      })
      this.isLoading = true
      try {
        const query = await this.$api.invoice.receivable.show({
          pk: validation
        })
        this.receivable = query.data
        this.getSuborders(query.data.order)
      } catch (error) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    /**
     * Método que consulta las subórdenes que tiene la orden asociada a la
     * cuenta por pagar.
     *
     * @param {Object} pk ID de la orden de venta
     *
     * @returns {Array} Subórdenes de venta.
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    async getSuborders(pk) {
      this.loadingSuborders = true
      try {
        const query = await this.$api.sale.suborder.show({
          pk: pk,
          opt: { params: { order: true } }
        })
        this.allSuborders = query.data
        this.suborders = this.filterSuborders(query.data)
      } catch (error) {
        console.error(error)
      } finally {
        this.loadingSuborders = false
      }
    },
    /**
     * Método que filtra las subordenes que saldrán en el listado, solo se
     * mostrarán las que su total de pagos no sea igual al total de la suborden.
     *
     * @param {Array} [suborders=[]] Arreglo de todas las subórdenes asociadas
     *                               a la orden de venta de la cuenta por cobrar
     * @returns {Array}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    filterSuborders(suborders = []) {
      return suborders.filter((suborder) => {
        return Number(suborder.total) > this.sumPayments(suborder.allocations)
      })
    },
    /**
     * Método que formatea y devuelve la dirección del cliente
     *
     * @param {Object} address Datos de la dirección
     *
     * @returns {string} Cadena de la dirección concatenada en un string.
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    formatAddress(address) {
      let fullAdress = ''
      fullAdress += `${address.address1}, `

      if (address.address2) {
        fullAdress += `${address.address2}, `
      }

      fullAdress += `${address.city}, `
      fullAdress += address.state
      return fullAdress
    },
    /**
     * Método que formatea y devuelve la dirección del cliente
     *
     * @param {Array} selected Método que realiza la sumatoria de totales de las
     *                         subórdenes seleccionadas para el pago
     *
     * @returns {Number} Total de la sumatoria
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    sumSuborders(selected = []) {
      const sum = selected.reduce(
        (accumulator, currentValue) =>
          accumulator +
          Number(currentValue.total) -
          this.sumPayments(currentValue.allocations),
        0
      )
      return sum
    },
    /**
     * Método que suma los pagos asociados a una suborden
     *
     * @returns {Number}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    sumPayments(allocations = []) {
      const sum = allocations.reduce(
        (accumulator, currentValue) => accumulator + Number(currentValue.debt),
        0
      )
      return sum
    },
    /**
     * Método que selecciona todas las subórdenes del listado
     * @returns {Void}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    selectAllSuborders() {
      this.suborderSelected = [...this.suborders]
    },
    /**
     * Método que selecciona una suborden específica del listado
     *
     * @param {Number} suborderPK ID de la suborden de venta que se seleccionará
     *
     * @returns {Void}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    selectSuborder(suborderPK) {
      this.suborderSelected = this.suborders.filter((suborder) => {
        return suborder.pk === suborderPK
      })
    },
    /**
     * Método para redireccionar la aplicación a una orden de venta específica.
     * @param {Number} orderPK   ID de la orden de venta que se quiere
     *                              consultar.
     * @returns {Void}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    goToOrder(orderPK) {
      this.$router.push({
        name: 'sale-pk',
        params: {
          pk: this.codification({ code: orderPK })
        }
      })
    }
  },
  mounted() {
    this.getReceivable()
  }
}
</script>
<style lang="sass">
.theme--light
  .invoice--item
    box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)
.theme--dark
  .invoice--item
    border: 1px solid rgba(132, 132, 132, 0.29)
    border-left: 10px solid var(--v-primary-base)
.invoice--item
  border-radius: 15px
  border-left: 10px solid var(--v-primary-base)
  padding-bottom: 12px
  .invoice--item-info
    text-transform: uppercase
    font-weight: 900

.receivable-header-info
  padding: 10px
  background-color: rgba(132, 132, 132, 0.1)
  border-left: 10px solid var(--v-primary-base)
  border-radius: 10px
  p
    margin-bottom: 5px

.v-data-table-header
  tr > th > span
    padding: 0 5px
.v-data-table
  tbody > tr > td
    .v-btn.v-size--small
      .v-icon.v-icon
        font-size: 16px!important
</style>
